import Konva from "konva";
import { IMarkup, MarkupMode } from "../../IMarkup";
import { Viewer } from "../../../Viewer";
import { EventListener } from "../../../EventEmitter2";
import { MarkupColor } from "./MarkupColor";
import * as utils from "../../../Draggers/MeasureLineDragger/MeasureUtils";
import { OdBaseDragger } from "../../../Draggers/Common/OdBaseDragger";
import { IMarkupObject } from "../../Api/IMarkupObject";
import { KonvaLine } from "../../Api/Impl/Konva/KonvaLine";
import { KonvaText } from "../../Api/Impl/Konva/KonvaText";

export class KonvaMarkup implements IMarkup {
  private _isInitialized = false;
  private _viewer: Viewer;
  private _canvasOriginal: HTMLCanvasElement;
  private _canvasEvents: string[];
  private _emitEvent: EventListener;
  private _canvasMarkup: HTMLCanvasElement;
  private _markupIsActive: boolean;
  private _markupMode: MarkupMode;
  private _markupColor: MarkupColor;
  private _konvaStage: Konva.Stage;
  private _konvaLayer: Konva.Layer;
  private _konvaTransformer: Konva.Transformer;

  private _textInputRef: HTMLTextAreaElement;
  private _textInputPos: Konva.Vector2d;
  private _textInputAngle: number;
  private _markupContainer: HTMLDivElement;

  private readonly _markupContainerName = "markupContainer";
  private readonly _canvasMarkupName = "canvasMarkup";
  private readonly TEXT_FONT_FAMILY = "Calibri";

  public lineWidth = 4;

  initialize(viewer: Viewer, canvas: HTMLCanvasElement, canvasEvents: string[], eventEmit: EventListener): void {
    this._viewer = viewer;
    this._canvasOriginal = canvas;
    this._canvasEvents = canvasEvents;
    this._emitEvent = eventEmit;
    this._markupContainer = document.createElement("div");
    this._markupContainer.id = this._markupContainerName;
    this._markupContainer.style.position = "absolute";
    this._markupContainer.style.top = "0px";
    this._markupContainer.style.left = "0px";
    // to eliminate grey box during delete elements
    this._markupContainer.style.outline = "0px";

    const parentDiv = this._canvasOriginal.parentElement;
    if (!(parentDiv instanceof HTMLDivElement))
      throw new Error("Konva Markup: Error during Markup Initialization. Canvas parent element must be Div.");

    parentDiv.appendChild(this._markupContainer);

    this._canvasMarkup = document.createElement("canvas");
    this._canvasMarkup.id = this._canvasMarkupName;
    this._markupContainer.appendChild(this._canvasMarkup);
    this.resizeCanvas();

    this._canvasEvents.forEach((x) => parentDiv.addEventListener(x, this._emitEvent));
    this._markupColor = new MarkupColor(255, 0, 0);
    this._markupIsActive = false;

    this.initializeKonva();

    this._viewer.on("changeactivedragger", (e) => {
      this._markupContainer.className = this._canvasOriginal.className
        .split(" ")
        .filter((x) => !x.startsWith("oda-cursor-"))
        .concat(`oda-cursor-${e.data.toLowerCase()}`)
        .join(" ");

      this.removeTextInput();

      if (e.data === MarkupMode.Line) {
        this._markupMode = MarkupMode.Line;
        this._markupIsActive = true;
      } else if (e.data === MarkupMode.Text) {
        this._markupMode = MarkupMode.Text;
        this._markupIsActive = true;
      } else {
        this._markupIsActive = false;
        this._konvaTransformer.nodes([]);
      }
    });
    this._isInitialized = true;
  }

  getDraggers(): Map<string, typeof OdBaseDragger> {
    return null;
  }

  resizeCanvas(): void {
    this._canvasMarkup.style.touchAction = this._canvasOriginal.style.touchAction;
    this._canvasMarkup.style.width = this._canvasOriginal.style.width;
    this._canvasMarkup.style.height = this._canvasOriginal.style.height;
    this._konvaStage?.width(this._canvasOriginal.width);
    this._konvaStage?.height(this._canvasOriginal.height);
  }

  clearOverlay(): void {
    this.removeTextInput();
    this._konvaTransformer.nodes([]);
    this._konvaLayer.find(MarkupMode.Line).forEach((x) => x.destroy());
    this._konvaLayer.find(MarkupMode.Text).forEach((x) => x.destroy());
  }

  getMarkupColor(): { r: number; g: number; b: number } {
    return this._markupColor.RGB;
  }

  setMarkupColor(r: number, g: number, b: number): void {
    this._markupColor.setColor(r, g, b);
  }

  colorizeAllMarkup(r = 255, g = 0, b = 0): void {
    this._konvaLayer.find(MarkupMode.Line).forEach((x) => (x.attrs.stroke = new MarkupColor(r, g, b).HexColor));
    this._konvaLayer.find(MarkupMode.Text).forEach((x) => (x.attrs.fill = new MarkupColor(r, g, b).HexColor));
    this._konvaLayer.draw();
  }

  drawViewpoint(viewpoint: any): void {
    function getLogicalPoint3dAsArray(point3d) {
      return [point3d.x, point3d.y, point3d.z];
    }

    if (!this._viewer.visualizeJs) return;

    const visLib = this._viewer.visLib();
    const visViewer = visLib.getViewer();
    const activeView = visViewer.activeView;

    this._viewer.resetActiveDragger();
    this._viewer.clearSlices();
    this._viewer.clearOverlay();

    if (viewpoint.orthogonal_camera) {
      activeView.setView(
        getLogicalPoint3dAsArray(viewpoint.orthogonal_camera.view_point),
        getLogicalPoint3dAsArray(viewpoint.orthogonal_camera.direction),
        getLogicalPoint3dAsArray(viewpoint.orthogonal_camera.up_vector),
        viewpoint.orthogonal_camera.field_width,
        viewpoint.orthogonal_camera.field_height,
        true
      );
    }

    this._viewer.syncOverlay();

    const markupColor = viewpoint.custom_fields.markup_color || { r: 255, g: 0, b: 0 };
    this.setMarkupColor(markupColor.r, markupColor.g, markupColor.b);

    if (viewpoint.clipping_planes) {
      for (const plane of viewpoint.clipping_planes) {
        const cuttingPlane = new visLib.OdTvPlane();
        cuttingPlane.set(getLogicalPoint3dAsArray(plane.location), getLogicalPoint3dAsArray(plane.direction));

        activeView.addCuttingPlane(cuttingPlane);
        activeView.setEnableCuttingPlaneFill(true, 0x66, 0x66, 0x66);
      }
    }

    this.loadMarkup(viewpoint);

    this._viewer.update();
  }

  createViewpoint(): object {
    function getLogicalPoint3dFromArray(array) {
      return { x: array[0], y: array[1], z: array[2] };
    }

    if (!this._viewer.visualizeJs) return {};

    const visLib = this._viewer.visLib();
    const visViewer = visLib.getViewer();
    const activeView = visViewer.activeView;

    const viewpoint = {
      lines: [],
      texts: [],
      clipping_planes: [],
    } as any;

    viewpoint.orthogonal_camera = {
      view_point: getLogicalPoint3dFromArray(activeView.viewPosition),
      direction: getLogicalPoint3dFromArray(activeView.viewTarget),
      up_vector: getLogicalPoint3dFromArray(activeView.upVector),
      field_width: activeView.viewFieldWidth,
      field_height: activeView.viewFieldHeight,
    };

    for (let i = 0; i < activeView.numCuttingPlanes(); i++) {
      const cuttingPlane = activeView.getCuttingPlane(i);

      const plane = {
        location: getLogicalPoint3dFromArray(cuttingPlane.getOrigin()),
        direction: getLogicalPoint3dFromArray(cuttingPlane.normal()),
      };

      viewpoint.clipping_planes.push(plane);
    }

    viewpoint.snapshot = {
      data: this._canvasMarkup ? this.combineMarkupWithDrawing() : visLib.canvas.toDataURL("image/jpeg", 0.25),
    };

    viewpoint.custom_fields = {
      markup_color: this.getMarkupColor(),
    };

    const markupLines = this.getMarkupLines();
    if (markupLines && markupLines.length > 0) {
      markupLines?.forEach((markupLine) => {
        const line = {
          points: [],
        };
        for (const point of markupLine.points) {
          line.points.push(getLogicalPoint3dFromArray(point));
        }
        viewpoint.lines.push(line);
      });
    }

    const markupTexts = this.getMarkupTexts();
    if (markupTexts && markupTexts.length > 0) {
      markupTexts?.forEach((markupText) => {
        const text = {
          position: getLogicalPoint3dFromArray(markupText.position),
          text: markupText.text,
          angle: markupText.rotation ?? 0,
          text_size: markupText.size,
        };
        viewpoint.texts.push(text);
      });
    }

    viewpoint.description = new Date().toDateString();
    return viewpoint;
  }

  dispose(): void {
    if (!this._isInitialized) return;

    this.clearOverlay();
    this._canvasEvents.forEach((x) => this._markupContainer.parentElement.removeEventListener(x, this._emitEvent));
    this._isInitialized = false;
  }

  createObject(type: string, params: any): IMarkupObject {
    let object;
    // TODO: factory?
    if (type === "line") {
      object = new KonvaLine(params);
    } else if (type === "text") {
      object = new KonvaText(params);
    } else {
      throw new Error("Markup CreateObject - unsupported type has been detected.");
    }
    this.addObject(object);
    return object;
  }

  addObject(object: IMarkupObject): void {
    this._konvaLayer.add(object.ref());
  }

  getObjects(): IMarkupObject[] {
    let objects = [];
    this._konvaLayer.find(MarkupMode.Line).forEach((line) => {
      objects.push(new KonvaLine(null, line));
    });

    this._konvaLayer.find(MarkupMode.Text).forEach((text) => {
      objects.push(new KonvaText(null, text));
    });

    return objects;
  }

  getSelectedObjects(): IMarkupObject[] {
    let objects = [];

    this._konvaTransformer.nodes().forEach((obj) => {
      if (obj.className === "Line") {
        objects.push(new KonvaLine(null, obj));
      }

      if (obj.className === "Text") {
        objects.push(new KonvaText(null, obj));
      }
    });

    return objects;
  }

  selectObjects(objects: IMarkupObject[]) {
    const selectedObjs = this._konvaTransformer.nodes().concat(objects.map((x) => x.ref()));
    this._konvaTransformer.nodes(selectedObjs);
  }

  clearSelected(): void {
    this._konvaTransformer.nodes([]);
  }

  private initializeKonva(): any {
    // first we need Konva core things: stage and layer
    this._konvaStage = new Konva.Stage({
      container: this._markupContainerName,
      width: this._canvasMarkup.width,
      height: this._canvasMarkup.height,
    });
    const stage = this._konvaStage;
    const layer = new Konva.Layer({ pixelRation: window.devicePixelRatio });
    stage.add(layer);
    this._konvaLayer = layer;

    const tr = new Konva.Transformer({
      shouldOverdrawWholeArea: false,
    });
    this._konvaTransformer = tr;
    layer.add(tr);

    let isPaint = false;
    let lastLine;

    stage.on("mousedown touchstart", (e) => {
      // do nothing if we mousedown on any shape
      if (!this._markupIsActive || e.target !== stage || this._markupMode === MarkupMode.Text) return;

      if (e.target === stage && tr.nodes().length > 0) {
        tr.nodes([]);
        return;
      }

      const pos = stage.getPointerPosition();
      // add point twice, so we have some drawings even on a simple click
      lastLine = this.addLine([pos.x, pos.y, pos.x, pos.y]);

      isPaint = true;
    });

    stage.on("mouseup touchend", (e) => {
      if (!this._markupIsActive) return;
      isPaint = false;
    });

    stage.on("mousemove touchmove", (e) => {
      if (!this._markupIsActive) return;
      if (!isPaint) {
        return;
      }

      // prevent scrolling on touch devices
      //e.evt.preventDefault();

      const pos = stage.getPointerPosition();
      const newPoints = lastLine.points().concat([pos.x, pos.y]);
      lastLine.points(newPoints);
    });

    // clicks should select/deselect shapes
    stage.on("click tap", (e) => {
      if (!this._markupIsActive) return;

      // if click on empty area - remove all selections
      if (e.target === stage) {
        if (this._markupMode === MarkupMode.Text) {
          if (this._textInputRef) this.addText(this._textInputRef.value, this._textInputPos, this._textInputAngle);
          else if (tr.nodes().length === 0) {
            const pos = stage.getPointerPosition();
            this.createTextInput(pos, e.evt.pageX, e.evt.pageY, 0, null);
          }
        }
        tr.nodes([]);
        return;
      }

      if (e.target.className === "Text" && tr.nodes().length === 1 && tr.nodes()[0] === e.target) {
        if (this._textInputRef) this.addText(this._textInputRef.value, this._textInputPos, this._textInputAngle);
        else
          this.createTextInput(
            { x: e.target.attrs.x, y: e.target.attrs.y },
            e.evt.pageX,
            e.evt.pageY,
            e.target.attrs.rotation,
            e.target.attrs.text
          );
        return;
      } else {
        this.removeTextInput();
      }

      // do we pressed shift or ctrl?
      const metaPressed = e.evt.shiftKey || e.evt.ctrlKey || e.evt.metaKey;
      const isSelected = tr.nodes().indexOf(e.target) >= 0;

      if (!metaPressed && !isSelected) {
        // if no key pressed and the node is not selected
        // select just one
        tr.nodes([e.target]);
      } else if (metaPressed && isSelected) {
        // if we pressed keys and node was selected
        // we need to remove it from selection:
        const nodes = tr.nodes().slice(); // use slice to have new copy of array
        // remove node from array
        nodes.splice(nodes.indexOf(e.target), 1);
        tr.nodes(nodes);
      } else if (metaPressed && !isSelected) {
        // add the node into selection
        const nodes = tr.nodes().concat([e.target]);
        tr.nodes(nodes);
      }
    });

    const container = stage.container();
    container.tabIndex = 1;
    // focus it
    // also stage will be in focus on its click
    container.focus();

    container.addEventListener("keydown", (e) => {
      if (!this._markupIsActive) return;
      if (e.code === "Delete") {
        const trNodes = this._konvaTransformer.nodes();
        if (trNodes.length > 0) {
          this._konvaTransformer.nodes().forEach((x) => x.destroy());
          this._konvaTransformer.nodes([]);
        }
        layer.draw();
        return;
      }
      e.preventDefault();
    });
  }

  private getMarkupLines() {
    const lines = [];
    this._konvaLayer.find(MarkupMode.Line).forEach((line) => {
      const linePoints = line.attrs.points;
      if (!linePoints) return;
      const worldPoints = [];
      const absoluteTransform = line.getAbsoluteTransform();
      for (let i = 0; i < linePoints.length; i += 2) {
        // https://stackoverflow.com/a/57641487 - check answer's comments
        const atPoint = absoluteTransform.point({ x: linePoints[i], y: linePoints[i + 1] });
        const worldPoint = this._viewer
          .visViewer()
          .screenToWorld(atPoint.x * window.devicePixelRatio, atPoint.y * window.devicePixelRatio);
        worldPoints.push(worldPoint);
      }
      lines.push({ points: worldPoints });
    });

    return lines;
  }

  private getMarkupTexts() {
    const texts = [];

    const textSize = 0.02;
    let textScale = 1.0;

    const projMtrx = this._viewer.visViewer().activeView.projectionMatrix;
    const mtrxNumber = projMtrx.get(1, 1);
    const tol = 1.0e-6;
    if (!(mtrxNumber < tol && mtrxNumber > -tol)) {
      textScale = 1 / mtrxNumber;
    }

    this._konvaLayer.find(MarkupMode.Text).forEach((text) => {
      if (!text) return;
      texts.push({
        position: this._viewer
          .visViewer()
          .screenToWorld(text.attrs.x * window.devicePixelRatio, text.attrs.y * window.devicePixelRatio),
        text: text.attrs.text,
        rotation: text.attrs.rotation && Math.abs(text.attrs.rotation) > tol ? text.attrs.rotation : 0,
        size: textSize * textScale,
      });
    });

    return texts;
  }

  private loadMarkup(viewpoint) {
    const lines = viewpoint.lines;
    lines?.forEach((line) => {
      const linePoints = [];
      line.points.forEach((point) => {
        const screenPoint = utils.worldToScreen(
          [point.x, point.y, point.z],
          this._viewer.visualizeJs,
          this._viewer.visViewer()
        );
        linePoints.push(screenPoint.x);
        linePoints.push(screenPoint.y);
      });

      this.addLine(linePoints);
    });

    const texts = viewpoint.texts;
    texts?.forEach((textJSON) => {
      const screenPoint = utils.worldToScreen(
        [textJSON.position.x, textJSON.position.y, textJSON.position.z],
        this._viewer.visualizeJs,
        this._viewer.visViewer()
      );
      this.addText(textJSON.text, screenPoint, textJSON.angle);
    });
  }

  private combineMarkupWithDrawing() {
    const trNodes = this._konvaTransformer.nodes();
    if (trNodes.length > 0) {
      this._konvaTransformer.nodes([]);
    }

    const tempCanvas = document.createElement("canvas");
    tempCanvas.height = this._canvasOriginal.height;
    tempCanvas.width = this._canvasOriginal.width;
    const ctx = tempCanvas.getContext("2d");
    ctx.drawImage(this._canvasOriginal, 0, 0);
    ctx.drawImage(this._konvaStage.toCanvas({ pixelRatio: window.devicePixelRatio }), 0, 0);
    return tempCanvas.toDataURL("image/jpeg", 0.25);
  }

  private addLine(linePoints: number[]): Konva.Line | void {
    if (!linePoints || linePoints.length === 0) return;
    const konvaLine = new Konva.Line({
      stroke: this._markupColor.HexColor,
      strokeWidth: this.lineWidth,
      globalCompositeOperation: "source-over",
      // round cap for smoother lines
      lineCap: "round",
      lineJoin: "round",
      points: linePoints,
      draggable: true,
      strokeScaleEnabled: false,
    });

    this._konvaLayer.add(konvaLine);
    return konvaLine;
  }

  private createTextInput(pos: Konva.Vector2d, inputX: number, inputY: number, angle: number, text: string): void {
    if (!this._textInputRef) {
      this._textInputPos = pos;
      this._textInputAngle = angle;
      this._textInputRef = document.createElement("textarea");
      this._textInputRef.style.zIndex = "9999";
      this._textInputRef.style.position = "absolute";
      this._textInputRef.style.display = "block";
      this._textInputRef.style.top = inputY + "px";
      this._textInputRef.style.left = inputX + "px";
      this._textInputRef.onkeydown = (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          this.addText(this._textInputRef.value, this._textInputPos, this._textInputAngle);
        }
        if (event.key === "Escape") {
          event.preventDefault();
          this.removeTextInput();
        }
      };
      if (text) this._textInputRef.value = text;
      document.body.appendChild(this._textInputRef);

      setTimeout(() => {
        this._textInputRef.focus();
      }, 50);
    } else {
      this.removeTextInput();
    }
  }

  private removeTextInput(): void {
    this._textInputRef?.remove();
    this._textInputRef = null;
    this._textInputPos = null;
    this._textInputAngle = 0;
  }

  private addText(specifiedText: string, pos: Konva.Vector2d, angle: number): void {
    if (specifiedText) {
      const konvaText = new Konva.Text({
        x: pos.x,
        y: pos.y,
        text: specifiedText,
        fontSize: 34,
        fontFamily: this.TEXT_FONT_FAMILY,
        fill: this._markupColor.HexColor,
        align: "left",
        draggable: true,
        rotation: angle,
      });

      this._konvaLayer.add(konvaText);
    }

    const trNodes = this._konvaTransformer.nodes();
    if (trNodes.length > 0) {
      // in case of edit - remove old Konva.Text object
      trNodes[0].destroy();
      this._konvaTransformer.nodes([]);
    }

    this.removeTextInput();

    return;
  }
}
